// CSV export (RFC 4180: CRLF line endings, quote fields containing
// comma/quote/newline, double embedded quotes). UTF-8 BOM so Excel opens
// non-ASCII names correctly.
import type { Employee, Shift, Team } from './types'
import { hoursByEmployee, round2 } from './hours'
import { weekDates } from './time'

export function toCsv(rows: (string | number | boolean | null | undefined)[][]): string {
  const lines = rows.map((row) => row.map(csvField).join(','))
  return '﻿' + lines.join('\r\n') + '\r\n'
}

function csvField(v: string | number | boolean | null | undefined): string {
  if (v == null) return ''
  const s = String(v)
  if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"'
  return s
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export interface ShiftCsvContext {
  departmentName: string
  weekStart: string
  teamsById: Map<string, Team>
}

/** Shape 1 — one row per shift/assignment. */
export function shiftsCsv(shifts: Shift[], ctx: ShiftCsvContext): string {
  const header = [
    'week_start',
    'date',
    'day',
    'department',
    'location',
    'position',
    'shift_start',
    'shift_end',
    'crosses_midnight',
    'hours',
    'assignee_name',
    'assignee_email',
    'status',
    'is_open',
    'note',
    'acknowledged_at',
  ]
  const rows = shifts
    .filter((s) => s.status !== 'cancelled')
    .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
    .map((s) => [
      ctx.weekStart,
      s.date,
      DAY_NAMES[new Date(s.date + 'T12:00:00Z').getUTCDay()],
      ctx.departmentName,
      ctx.teamsById.get(s.teamId)?.name ?? s.teamId,
      s.positionName,
      `${s.date} ${s.startTime}`,
      s.endTime,
      s.endsNextDay,
      s.durationHours.toFixed(2),
      s.assigneeName ?? '',
      s.assigneeEmail ?? '',
      s.status,
      s.status === 'open',
      s.notes ?? '',
      s.ackedAt ?? '',
    ])
  return toCsv([header, ...rows])
}

/** Shape 2 — one row per employee: per-day hours, weekly total, OT flag. */
export function hoursSummaryCsv(
  shifts: Shift[],
  employees: Pick<Employee, 'email' | 'workspace' | 'homeTeamId'>[],
  weekStart: string,
  thresholdHours: number,
  teamsById: Map<string, Team>,
): string {
  const dates = weekDates(weekStart)
  const header = [
    'week_start',
    'employee_name',
    'employee_email',
    'home_team',
    'sun_hours',
    'mon_hours',
    'tue_hours',
    'wed_hours',
    'thu_hours',
    'fri_hours',
    'sat_hours',
    'total_hours',
    'ot_flag',
  ]
  const active = shifts.filter((s) => s.status === 'assigned' && s.assigneeEmail)
  const totals = hoursByEmployee(active)
  const byEmail = new Map(employees.map((e) => [e.email, e]))
  const emails = [...totals.keys()].sort()
  const rows = emails.map((email) => {
    const emp = byEmail.get(email)
    const perDay = dates.map((d) =>
      round2(
        active
          .filter((s) => s.assigneeEmail === email && s.date === d)
          .reduce((sum, s) => sum + s.durationHours, 0),
      ).toFixed(2),
    )
    const total = totals.get(email) ?? 0
    return [
      weekStart,
      emp?.workspace.displayName ?? email,
      email,
      emp?.homeTeamId ? (teamsById.get(emp.homeTeamId)?.name ?? '') : '',
      ...perDay,
      total.toFixed(2),
      total > thresholdHours,
    ]
  })
  return toCsv([header, ...rows])
}

/** Trigger a browser download of a CSV string. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
